import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import CircularProgress from "@mui/material/CircularProgress";
import { obtenerListaCotizaciones } from "../../../store/cotizaciones/thunkCotizaciones";
import { UnificarModales } from "../Modales/UnificarModales";
import { DetalleCotizacionModal } from "./DetalleCotizacionModal";
import "./ListaCotizacion.css";

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: "48px",
        borderRadius: "14px",
        borderColor: state.isFocused ? "#0f766e" : "#d7dee7",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(15, 118, 110, 0.12)" : "none",
        backgroundColor: "#ffffff",
        "&:hover": {
            borderColor: "#0f766e",
        },
    }),
    placeholder: (base) => ({
        ...base,
        color: "#6b7280",
    }),
    menu: (base) => ({
        ...base,
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.14)",
        zIndex: 20,
    }),
};

const DEFAULT_PAGINATION = {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
};

const DEFAULT_REMOTE_FILTERS = {
    empresas: [],
    admins: [],
    estados: [],
};

const getEstadoClassName = (estado) => {
    switch (estado) {
        case "Confirmada":
            return "cotizaciones-list-status cotizaciones-list-status--confirmada";
        case "Pendiente":
            return "cotizaciones-list-status cotizaciones-list-status--pendiente";
        case "Enviado":
            return "cotizaciones-list-status cotizaciones-list-status--enviado";
        case "Tentativa":
            return "cotizaciones-list-status cotizaciones-list-status--tentativa";
        default:
            return "cotizaciones-list-status cotizaciones-list-status--default";
    }
};

const formatearFechaModi = (fecha) =>
    new Date(fecha).toLocaleDateString("es-ES", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
    });

const formatearFechaEvento = (fecha) => fecha || "Fecha por definir";

const truncarTexto = (texto = "", limite = 30) => (texto.length > limite ? `${texto.substring(0, limite - 3)}...` : texto);

const toOptions = (values = []) => values.map((value) => ({ value, label: value }));

/**
 * Hook mínimo para no disparar request por cada tecla.
 *
 * @param {string} value - Valor a estabilizar.
 * @param {number} delay - Espera en milisegundos.
 * @returns {string} Valor con debounce.
 */
const useDebouncedValue = (value, delay = 300) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => setDebouncedValue(value), delay);
        return () => window.clearTimeout(timeoutId);
    }, [delay, value]);

    return debouncedValue;
};

export const ListaCotizacion = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [dataTable, setDataTable] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    const [remoteFilters, setRemoteFilters] = useState(DEFAULT_REMOTE_FILTERS);
    const [openModal, setOpenModal] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const [showConfirmed, setShowConfirmed] = useState(false);
    const [filters, setFilters] = useState({
        nombre_evento_coti: "",
        id_consecutivo_coti: "",
        nombre_empresa: null,
        name_admin: null,
        Estado_coti: null,
    });

    const debouncedNombreEvento = useDebouncedValue(filters.nombre_evento_coti);
    const debouncedConsecutivo = useDebouncedValue(filters.id_consecutivo_coti);

    const fetchCotizaciones = useCallback(async () => {
        setLoading(true);

        try {
            const response = await dispatch(
                obtenerListaCotizaciones({
                    page: currentPage,
                    limit: pageSize,
                    includeConfirmed: showConfirmed,
                    filters: {
                        nombre_evento_coti: debouncedNombreEvento,
                        id_consecutivo_coti: debouncedConsecutivo,
                        nombre_empresa: filters.nombre_empresa?.value || "",
                        name_admin: filters.name_admin?.value || "",
                        Estado_coti: filters.Estado_coti?.value || "",
                    },
                }),
            );

            setDataTable(response?.data || []);
            setPagination(response?.pagination || DEFAULT_PAGINATION);
            setRemoteFilters(response?.filters || DEFAULT_REMOTE_FILTERS);
        } catch (error) {
            console.error("Error al obtener la lista de cotizaciones:", error);
        } finally {
            setLoading(false);
        }
    }, [
        currentPage,
        debouncedConsecutivo,
        debouncedNombreEvento,
        dispatch,
        filters.Estado_coti,
        filters.name_admin,
        filters.nombre_empresa,
        pageSize,
        showConfirmed,
    ]);

    useEffect(() => {
        fetchCotizaciones();
    }, [fetchCotizaciones]);

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
        setCurrentPage(1);
    };

    const handlePageSizeChange = (event) => {
        setPageSize(Number(event.target.value));
        setCurrentPage(1);
    };

    const handleRowClick = (row) => {
        setSelectedData(row);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedData(null);
    };

    const generarCotizacion = () => {
        navigate("/cotizaciones/crear?data=0");
    };

    return (
        <div className="cotizaciones-list-view">
            <section className="cotizaciones-list-shell">
                <div className="cotizaciones-list-hero">
                    <div>
                        <span className="cotizaciones-list-eyebrow">Catalogo de cotizaciones</span>
                        <h1 className="cotizaciones-list-title">Cotizaciones de eventos y servicios</h1>
                        <p className="cotizaciones-list-subtitle">
                            Consulta cotizaciones, filtra por cliente, ejecutivo o estado y abre cualquier detalle con un clic.
                        </p>
                    </div>

                    <div className="cotizaciones-list-actions">
                        <button type="button" className="cotizaciones-list-btn cotizaciones-list-btn--primary" onClick={generarCotizacion}>
                            Generar cotizacion
                        </button>
                        <button
                            type="button"
                            className="cotizaciones-list-btn cotizaciones-list-btn--secondary"
                            onClick={() => {
                                setShowConfirmed((prev) => !prev);
                                setCurrentPage(1);
                            }}
                        >
                            {showConfirmed ? "Ver pendientes" : "Ver confirmadas"}
                        </button>
                        <div className="cotizaciones-list-btn cotizaciones-list-btn--ghost">
                            <UnificarModales />
                        </div>
                    </div>
                </div>

                <div className="cotizaciones-list-toolbar">
                    <div className="cotizaciones-list-filter-grid">
                        <div className="cotizaciones-list-field">
                            <label className="cotizaciones-list-label" htmlFor="cotizacionEventoFiltro">
                                Buscar evento
                            </label>
                            <input
                                id="cotizacionEventoFiltro"
                                type="text"
                                className="cotizaciones-list-input"
                                placeholder="Escribe nombre del evento"
                                value={filters.nombre_evento_coti}
                                onChange={(e) => handleFilterChange("nombre_evento_coti", e.target.value)}
                            />
                        </div>

                        <div className="cotizaciones-list-field">
                            <label className="cotizaciones-list-label" htmlFor="cotizacionConsecutivoFiltro">
                                Buscar consecutivo
                            </label>
                            <input
                                id="cotizacionConsecutivoFiltro"
                                type="text"
                                className="cotizaciones-list-input"
                                placeholder="Escribe ID consecutivo exacto"
                                value={filters.id_consecutivo_coti}
                                onChange={(e) => handleFilterChange("id_consecutivo_coti", e.target.value)}
                            />
                        </div>

                        <div className="cotizaciones-list-field">
                            <label className="cotizaciones-list-label">Empresa</label>
                            <Select
                                placeholder="Seleccionar empresa"
                                options={toOptions(remoteFilters.empresas)}
                                value={filters.nombre_empresa}
                                onChange={(value) => handleFilterChange("nombre_empresa", value)}
                                isClearable
                                styles={selectStyles}
                            />
                        </div>

                        <div className="cotizaciones-list-field">
                            <label className="cotizaciones-list-label">Ejecutivo</label>
                            <Select
                                placeholder="Seleccionar admin"
                                options={toOptions(remoteFilters.admins)}
                                value={filters.name_admin}
                                onChange={(value) => handleFilterChange("name_admin", value)}
                                isClearable
                                styles={selectStyles}
                            />
                        </div>

                        <div className="cotizaciones-list-field">
                            <label className="cotizaciones-list-label">Estado</label>
                            <Select
                                placeholder="Seleccionar estado"
                                options={toOptions(remoteFilters.estados)}
                                value={filters.Estado_coti}
                                onChange={(value) => handleFilterChange("Estado_coti", value)}
                                isClearable
                                styles={selectStyles}
                            />
                        </div>

                        <div className="cotizaciones-list-field cotizaciones-list-field--compact">
                            <label className="cotizaciones-list-label" htmlFor="cotizacionesPageSize">
                                Mostrar
                            </label>
                            <select
                                id="cotizacionesPageSize"
                                className="cotizaciones-list-select"
                                value={pageSize}
                                onChange={handlePageSizeChange}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={30}>30</option>
                                <option value={50}>50</option>
                                <option value={70}>70</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="cotizaciones-list-table-card">
                    <div className="cotizaciones-list-table-scroll">
                        {loading ? (
                            <div className="cotizaciones-list-loading">
                                <CircularProgress />
                            </div>
                        ) : (
                            <table className="cotizaciones-list-table">
                                <thead>
                                    <tr>
                                        <th>Consecutivo</th>
                                        <th>Evento</th>
                                        <th>Empresa</th>
                                        <th>Ejecutivo</th>
                                        <th>Fecha evento</th>
                                        <th>Fecha creacion</th>
                                        <th>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dataTable.length > 0 ? (
                                        dataTable.map((row) => (
                                            <tr key={row.id_coti} className="cotizaciones-list-row" onClick={() => handleRowClick(row)}>
                                                <td>
                                                    <span className="cotizaciones-list-id">{row.id_consecutivo_coti}</span>
                                                </td>
                                                <td>
                                                    <span className="cotizaciones-list-event">{truncarTexto(row.nombre_evento_coti, 32)}</span>
                                                    <span className="cotizaciones-list-subtext">ID interno {row.id_coti}</span>
                                                </td>
                                                <td>{truncarTexto(row.nombre_empresa, 28)}</td>
                                                <td>{truncarTexto(row.name_admin, 24)}</td>
                                                <td>{truncarTexto(formatearFechaEvento(row.fecha_evento_coti), 28)}</td>
                                                <td>{formatearFechaModi(row.fecha_creacion_coti)}</td>
                                                <td>
                                                    <span className={getEstadoClassName(row.Estado_coti)}>{row.Estado_coti}</span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7">
                                                <div className="cotizaciones-list-empty">No hay cotizaciones para este filtro.</div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                <div className="cotizaciones-list-pagination">
                    <button
                        type="button"
                        className="cotizaciones-list-page-btn"
                        onClick={() => setCurrentPage((prev) => prev - 1)}
                        disabled={currentPage === 1 || loading}
                    >
                        Anterior
                    </button>
                    <div className="cotizaciones-list-page-indicator">
                        Pagina <strong>{pagination.page}</strong> de <strong>{pagination.totalPages}</strong>
                    </div>
                    <button
                        type="button"
                        className="cotizaciones-list-page-btn"
                        onClick={() => setCurrentPage((prev) => prev + 1)}
                        disabled={currentPage >= pagination.totalPages || loading}
                    >
                        Siguiente
                    </button>
                </div>
            </section>

            <DetalleCotizacionModal open={openModal} handleClose={handleCloseModal} data={selectedData} />
        </div>
    );
};
